<template>
  <div class="auth-page">
    <div class="auth-brand">
      <img
        src="/icons/icon.svg"
        alt=""
        class="auth-mark"
        width="56"
        height="56"
      >
      <p class="kicker">{{ t('nav.brand') }}</p>
    </div>
    <h1>{{ t('auth.loginTitle') }}</h1>
    <p class="lead">{{ t('auth.loginLead') }}</p>

    <form class="panel" @submit.prevent="submit">
      <label class="field">
        <span>{{ t('auth.identifier') }}</span>
        <input
          v-model="ident"
          type="text"
          autocomplete="username"
          :placeholder="t('auth.loginPlaceholder')"
          required
        />
      </label>
      <label class="field">
        <span>{{ t('auth.password') }}</span>
        <PasswordInput
          v-model="password"
          autocomplete="current-password"
          required
        />
      </label>
      <router-link class="forgot" :to="{ name: 'forgot-password' }">
        {{ t('auth.forgotLink') }}
      </router-link>
      <p v-if="errorKey" class="error">{{ t(errorKey) }}</p>
      <button type="submit" class="cta" :disabled="auth.busy">
        {{ t('auth.signIn') }}
      </button>
    </form>

    <p class="switch">
      {{ t('auth.noAccount') }}
      <router-link :to="registerTo">{{ t('auth.goRegister') }}</router-link>
    </p>

    <div class="discord-row">
      <DiscordJoinButton />
      <SupportButton />
    </div>

    <div class="auth-lang">
      <LanguageSelect />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LanguageSelect from '../components/LanguageSelect.vue'
import { useI18n } from '../i18n/t'
import { ApiError } from '../api/http'
import { useAuthStore } from '../stores/auth'
import DiscordJoinButton from '../components/DiscordJoinButton.vue'
import PasswordInput from '../components/PasswordInput.vue'
import SupportButton from '../components/SupportButton.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ident = ref('')
const password = ref('')
const errorKey = ref('')

const registerTo = computed(() => ({
  name: 'register',
  query: route.query.redirect ? { redirect: route.query.redirect } : {},
}))

function errorToKey(err: unknown): string {
  if (err instanceof ApiError) {
    const code = err.code
    if (code === 'invalid_credentials') return 'auth.errors.invalid_credentials'
    if (code === 'rate_limited') return 'auth.errors.rate_limited'
    if (code === 'database_unavailable') return 'auth.errors.database_unavailable'
    if (code === 'validation') return 'auth.errors.validation'
    if (err.status === 503) return 'auth.errors.database_unavailable'
  }
  return 'auth.errors.network'
}

function safeRedirect(): string {
  const raw = route.query.redirect
  if (typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//')) {
    return raw
  }
  return '/'
}

async function submit(): Promise<void> {
  errorKey.value = ''
  try {
    await auth.login({ login: ident.value, password: password.value })
    await router.replace(safeRedirect())
  } catch (err) {
    errorKey.value = errorToKey(err)
  }
}
</script>

<style scoped>
.auth-page {
  width: min(440px, 100%);
  margin: 0 auto;
  padding: 48px 16px 64px;
  font-family: Roboto, system-ui, sans-serif;
  color: var(--md-sys-color-on-surface);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.auth-mark {
  width: 56px;
  height: 56px;
  border-radius: 14px;
}

.kicker {
  margin: 0 0 8px;
  color: var(--md-sys-color-primary);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 8px;
  font-size: 36px;
  line-height: 44px;
  font-weight: 400;
}

.lead {
  margin: 0 0 24px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  line-height: 20px;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-1);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--md-sys-color-on-surface-variant);
}

.field input {
  min-height: 44px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline);
  border-radius: 4px 4px 0 0;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 16px;
  font-weight: 400;
}

.field input:focus {
  outline: none;
  border-bottom-width: 2px;
  border-bottom-color: var(--md-sys-color-primary);
}

.cta {
  min-height: 44px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.cta:disabled {
  opacity: 0.6;
  cursor: wait;
}

.forgot {
  align-self: flex-start;
  color: var(--md-sys-color-primary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.error {
  margin: 0;
  color: #e07070;
  font-size: 14px;
  line-height: 20px;
}

.switch {
  margin: 20px 0 0;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 14px;
  text-align: center;
}

.switch a {
  color: var(--md-sys-color-primary);
  font-weight: 600;
  text-decoration: none;
}

.discord-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.auth-lang {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}
</style>
